/**
 * Utility for Hijri date calculation
 * Uses the Tabular Islamic Calendar algorithm
 */

export interface HijriDate {
    year: number;
    month: number;
    day: number;
}

export class DateUtil {
    /**
     * Convert Gregorian date to Hijri
     * Algorithm based on Kuwaiti Calendar (tabular Islamic calendar)
     */
    public static getHijriDate(date: Date): HijriDate {
        let day = date.getDate();
        let month = date.getMonth();
        let year = date.getFullYear();

        let m = month + 1;
        let y = year;
        if (m < 3) {
            y -= 1;
            m += 12;
        }

        let a = Math.floor(y / 100);
        let b = 2 - a + Math.floor(a / 4);
        if (y < 1583) b = 0;
        if (y == 1582) {
            if (m > 10) b = -10;
            if (m == 10) {
                b = 0;
                if (day > 4) b = -10;
            }
        }

        let jd = Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + day + b - 1524;

        let b1 = 0;
        if (jd > 2299160) {
            let a1 = Math.floor((jd - 1867216.25) / 36524.25);
            b1 = 1 + a1 - Math.floor(a1 / 4);
        }
        let bb = jd + b1 + 1524;
        let cc = Math.floor((bb - 122.1) / 365.25);
        let dd = Math.floor(365.25 * cc);
        let ee = Math.floor((bb - dd) / 30.6001);
        day = (bb - dd) - Math.floor(30.6001 * ee);
        month = ee - 1;
        if (ee > 13) {
            cc += 1;
            month = ee - 13;
        }
        year = cc - 4716;

        let iyear = 10631 / 30;
        let epochAsari = 1948085;
        let shift1 = 8.01 / 60;

        let z = jd - epochAsari;
        let cyc = Math.floor(z / 10631);
        z = z - 10631 * cyc;
        let j = Math.floor((z - shift1) / iyear);
        let iy = 30 * cyc + j;
        z = z - Math.floor(j * iyear + shift1);
        let im = Math.floor((z + 28.5001) / 29.5);
        if (im == 13) im = 12;
        let id = z - Math.floor(29.5001 * im - 29);

        return {
            year: iy,
            month: im,
            day: id
        };
    }

    /**
   * Check if it's already the new academic year
   * Default start: Ramadan (Month 9)
   */
    public static isNewAcademicYear(date: Date = new Date()): boolean {
        const hijri = this.getHijriDate(date);
        // Ramadan is month 9
        return hijri.month >= 9;
    }

    /**
     * Finds the Gregorian month index (1-12) of 1 Ramadan in a given Gregorian year.
     */
    public static getRamadanMonth(year: number): number {
        // Search through Gregorian months to find when Hijri month 9 (Ramadan) starts
        for (let m = 1; m <= 12; m++) {
            const start = this.getHijriDate(new Date(year, m - 1, 1));
            const end = this.getHijriDate(new Date(year, m, 0));
            // If Ramadan starts or exists within this Gregorian month
            if (start.month === 9 || end.month === 9) return m;
        }
        return 1; // Fallback
    }

    /**
     * Determines the transition month index (1-12) based on type.
     */
    public static getTransitionMonth(type: string, year: number): number {
        if (type === 'pondok') return this.getRamadanMonth(year);
        if (type === 'sekolah') return 4; // April
        return 7; // PSB / Default (July)
    }

    /**
     * Check if it's already the new academic year based on Gregorian month
     * Default start: April (Month 4)
     */
    public static isNewAcademicYearGregorian(monthThreshold: number = 4, date: Date = new Date()): boolean {
        const currentMonth = date.getMonth() + 1;
        return currentMonth >= monthThreshold;
    }
}
